import { useEffect, useRef } from 'react'
import { Select } from 'blaze-ui/Select/Select'
import { Item } from '@react-stately/collections'
import { TextInputField } from 'components/Form/TextInputField'
import { WordPressUser } from 'api/clients/BlogMetadataRecordsClient'
import Styles from '../../../BlogMetadata.module.scss'
import { TextFieldSize } from 'components/Form/useTextFieldCounter'

type WordPressAuthorSelectProps = {
  authors: WordPressUser[]
  selectedAuthorId: string | undefined
  onAuthorSelect: (authorId: string) => void
  placeholder?: string
  disabled?: boolean
  size?: TextFieldSize
}

export const WordPressAuthorSelect = ({
  authors,
  selectedAuthorId,
  onAuthorSelect,
  placeholder = 'Select an author',
  disabled = false,
  size = 'lg',
}: WordPressAuthorSelectProps) => {
  const isEmpty = authors.length === 0
  const isSingleAuthor = authors.length === 1
  const singleAuthor = isSingleAuthor ? authors[0] : null
  const singleAuthorId = singleAuthor?.id

  // Use ref to store the latest callback to avoid dependency issues
  // This prevents the useEffect from re-running when onAuthorSelect reference changes
  const onAuthorSelectRef = useRef(onAuthorSelect)
  useEffect(() => {
    onAuthorSelectRef.current = onAuthorSelect
  }, [onAuthorSelect])

  // Only depend on the author ID (primitive) instead of the whole object to prevent re-renders
  useEffect(() => {
    if (singleAuthorId && selectedAuthorId !== String(singleAuthorId)) {
      onAuthorSelectRef.current(String(singleAuthorId))
    }
  }, [singleAuthorId, selectedAuthorId])

  if (isSingleAuthor && singleAuthor) {
    return (
      <TextInputField
        wrapperClassName={Styles.inputField}
        label="Author"
        value={singleAuthor.name}
        onChange={() => { }}
        size={size}
        labelClassName={Styles.textFieldLabel}
        isReadOnly
        isDisabled={disabled}
      />
    )
  }

  return (
    <Select
      aria-label="Select author"
      placeholder={isEmpty ? 'No authors available' : placeholder}
      selectedKey={selectedAuthorId}
      onSelectionChange={(key) => onAuthorSelect(key as string)}
      isDisabled={isEmpty || disabled}
      useButtonWidth={true}
      useFullWidth={true}
      variant="secondary"
      size={size}
    >
      {authors.map((author) => (
        <Item key={String(author.id)} textValue={author.name}>
          {author.name}
        </Item>
      ))}
    </Select>
  )
}
